// LiveStreamUser.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import UserAvatar from '../container/UserAvatar.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import LiveStreamGiftDiamond from '../component/LiveStreamGiftDiamond.jsx';

import WhiteAddIconSrc from '../../img/ic-add-white-xs.svg';
import { ButtonId } from '../resource/mixpanel.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { getIsOnMacOS, getIsOnWindows } from '../resource/getUserAgent.js';

const isOnWindows = getIsOnWindows();
const isOnMacOS = getIsOnMacOS();

export class LiveStreamUser extends React.PureComponent {
  subscriptionReference = `LiveStreamUser-${uuidv4()}`;

  componentDidMount() {
    const {
      userId,
      username,
      fetchUser,
      toggleSubscribeOnlineStatus,
      fetchUserIsFollowing,
    } = this.props;
    this.nextTick = setTimeout(() => {
      if (userId && !username) {
        fetchUser({ userId });
      }
      toggleSubscribeOnlineStatus({
        userId,
        isCritical: true,
        shouldSubscribe: true,
        subscriptionReference: this.subscriptionReference,
      });
      fetchUserIsFollowing({ userId });
    });
  }

  componentWillUnmount() {
    const { userId, toggleSubscribeOnlineStatus } = this.props;

    clearTimeout(this.nextTick);
    toggleSubscribeOnlineStatus({
      userId,
      shouldSubscribe: false,
      subscriptionReference: this.subscriptionReference,
    });
  }

  handleToUserProfile = event => {
    const {
      isPrivateShowUnlocked,
      isPrivateStreamUnlocked,
      shouldShowExitAlertModal,
      leaveLiveStreams,
      addModal,
    } = this.props;
    if ((isOnWindows && event.ctrlKey) || (isOnMacOS && event.metaKey)) return;

    if (
      shouldShowExitAlertModal &&
      (isPrivateShowUnlocked || isPrivateStreamUnlocked)
    ) {
      event.preventDefault();
      return addModal({
        id: 'LeaveLivestreamToUserProfileConfirmModal',
      });
    }
    return leaveLiveStreams();
  };

  renderOfflineUser = () => {
    const { t, userId, displayedUsername, username } = this.props;
    return (
      <StyledLiveStreamUserOffline>
        {userId && (
          <UserProfileLink
            to={`/user/${userId}`}
            onClick={this.handleToUserProfile}
            alt={displayedUsername}
            aria-label={displayedUsername}
          >
            <UserAvatar id={userId} size={72} />
          </UserProfileLink>
        )}
        <OfflineUsername>
          <UserProfileLink
            to={`/user/${userId}`}
            onClick={this.handleToUserProfile}
            alt={displayedUsername}
            data-element_id={ButtonId.Detail.ContentDetailName}
            data-tracking_payload={{
              'user.id': userId,
              'user.username': username,
            }}
          >
            {displayedUsername}
          </UserProfileLink>
        </OfflineUsername>
        <GoToProfileButton
          to={`/user/${userId}`}
          data-element_id={ButtonId.LiveStream.ButtonOfflineToProfile}
          data-tracking_payload={{
            'user.id': userId,
          }}
        >
          {t('view_profile', {
            ns: TranslationNamespace.LIVESTREAM,
          })}
        </GoToProfileButton>
      </StyledLiveStreamUserOffline>
    );
  };
  renderOnlineUser = () => {
    const {
      displayedUsername,
      username,
      userId,
      isFollowing,
      earnings,
      followUser,
    } = this.props;
    return (
      <StyledLiveStreamUser isFollowing={isFollowing}>
        {userId && (
          <UserProfileLink
            to={`/user/${userId}`}
            onClick={this.handleToUserProfile}
            alt={displayedUsername}
            aria-label={displayedUsername}
          >
            <UserAvatar id={userId} size={36} />
          </UserProfileLink>
        )}
        {displayedUsername && (
          <UserInfo>
            <UserProfileLink
              to={`/user/${userId}`}
              onClick={this.handleToUserProfile}
              alt={displayedUsername}
              data-element_id={ButtonId.Detail.ContentDetailName}
              data-tracking_payload={{
                'user.id': userId,
                'user.username': username,
              }}
            >
              <Username>{displayedUsername}</Username>
            </UserProfileLink>
            <LiveStreamGiftDiamond giftDiamondCount={earnings} />
          </UserInfo>
        )}
        {!isFollowing && (
          <FollowButton
            data-element_id={ButtonId.All.ButtonFollow}
            data-tracking_payload={{
              'user.id': userId,
            }}
            onClick={() => {
              followUser({ userId });
            }}
          >
            <img src={WhiteAddIconSrc} alt="Follow User" />
          </FollowButton>
        )}
      </StyledLiveStreamUser>
    );
  };
  render() {
    const { offline } = this.props;
    if (offline) return this.renderOfflineUser();
    else return this.renderOnlineUser();
  }
}

LiveStreamUser.propTypes = {
  t: PropTypes.func.isRequired,
  earnings: PropTypes.number,
  userId: PropTypes.string,
  username: PropTypes.string,
  displayedUsername: PropTypes.string,
  isFollowing: PropTypes.bool,
  isPrivateShowUnlocked: PropTypes.bool,
  isPrivateStreamUnlocked: PropTypes.bool,
  shouldShowExitAlertModal: PropTypes.bool,
  fetchUser: PropTypes.func,
  followUser: PropTypes.func,
  offline: PropTypes.bool,
  leaveLiveStreams: PropTypes.func,
  fetchUserIsFollowing: PropTypes.func,
  addModal: PropTypes.func,
  toggleSubscribeOnlineStatus: PropTypes.func,
};

LiveStreamUser.defaultProps = {
  earnings: null,
  userId: '',
  username: '',
  displayedUsername: '',
  isFollowing: false,
  isPrivateShowUnlocked: false,
  isPrivateStreamUnlocked: false,
  shouldShowExitAlertModal: false,
  fetchUser: () => null,
  followUser: () => null,
  offline: false,
  leaveLiveStreams: () => null,
  fetchUserIsFollowing: () => null,
  addModal: () => null,
  toggleSubscribeOnlineStatus: () => null,
};

const UserProfileLink = styled(Link)``;

const StyledLiveStreamUser = styled.div`
  max-width: 36.8%;
  max-width: 240px;
  min-width: ${({ isFollowing }) => (isFollowing ? '118px' : '138px')};

  display: inline-flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
  padding: 2px 4px;
  border-radius: 22px;
  background-color: rgba(35, 35, 35, 0.4);
  overflow: hidden;
`;

const UserInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-width: 70px;
  margin: 0 4px;
  color: #ffffff;
  text-align: left;
  overflow: hidden;
`;

const Username = styled.h2`
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: #ffffff;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const FollowButton = styled.button`
  ${ResetButtonStyle}
  margin-left: 4px;
  display: block;
  width: 20px;
  height: 20px;
  min-width: 20px;
  outline: 0;
  background-color: #00d2be;
  border-radius: 50%;
  & > img {
    width: 100%;
    height: 100%;
  }
`;

const StyledLiveStreamUserOffline = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 100%;
`;

const OfflineUsername = styled.div`
  margin-top: 12px;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
`;

const GoToProfileButton = styled(Link)`
  display: block;
  width: 140px;
  height: 24px;
  background-color: #00d2be;
  border-radius: 24px;
  font-size: 14px;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default withTranslation()(LiveStreamUser);
